/** Прогноз на конец месяца вкладки «План» — три режима (чистые функции). Источник правды —
 *  docs/ozon-analytics.md «План/факт» → «Прогноз на конец месяца»; задача
 *  docs/todo/2026-10-22-plan-forecast-modes.md.
 *
 *  Обозначения (по дневному ряду `daily` бэкенда — у юрлица и у площадки итогом свой): T —
 *  `daily.to`, дата полных данных (юрлица — из `data_through`, площадки — min кабинетов с `active`);
 *  F — факт с 1-го по T (`daily.fact`), e — дней месяца по T (`daily.elapsed`), D — дней в месяце,
 *  R = D − e — осталось дней после T; y — `daily.values[код]`: до 28 полных дней по T (день без
 *  строк — 0, дни до первой даты юрлица в ряд не входят), n — его длина. Ряда нет (месяц не
 *  текущий) — F, e — факт и `days_elapsed` площадки.
 *
 *  - «по среднему» (`avg`):   F + F / e · R — средний день месяца на оставшиеся дни;
 *  - «по 7 дням» (`last7`):   F + (y[n−7] + … + y[n−1]) / 7 · R — средний день последних 7 дней
 *    (по одному каждому дню недели);
 *  - «по тренду» (`trend`):   F + Σ_{k=1..R} (a + b·(27 + k)), где y = a + b·x — прямая МНК по
 *    последним 28 значениям, x = 0…27: b = Σ(x − x̄)(y − ȳ) / Σ(x − x̄)², a = ȳ − b·x̄ (x̄ = 13,5);
 *    сумма = R·a + b·(27·R + R(R + 1)/2) — продолжение прямой 4 недель на оставшиеся дни.
 *
 *  «Сейчас в день» (`perDay`, текущий дневной темп режима): avg — F / e; last7 — то же среднее 7 дней;
 *  trend — значение прямой на следующий день a + b·28. Итог — Σ темпов участников.
 *
 *  R = 0 (месяц закрыт) — прогноз = F во всех режимах, темп 0 (оставшихся дней нет; в Σ «Все
 *  площадки» закрытая площадка даёт 0). Иначе режим недоступен → `{value: null, reason}` (а не
 *  расчёт по части дней): факта нет; e = 0 — только «по среднему» (делить не на что; у юрлица с
 *  данными, отстающими в прошлый месяц, «по 7 дням» и тренд считаются с F = 0, R = D); ряда нет;
 *  n < 7 (n < 28 для тренда) — «мало истории»; null в окне режима (день без себестоимости). */

export type ForecastMode = "avg" | "last7" | "trend";

export const FORECAST_MODES: { id: ForecastMode; label: string }[] = [
  { id: "avg", label: "по среднему" },
  { id: "last7", label: "по 7 дням" },
  { id: "trend", label: "по тренду" },
];

/** Режим по умолчанию (решение владельца 22.10.2026): в адрес не пишется. */
export const DEFAULT_FORECAST_MODE: ForecastMode = "last7";

export const LAST_DAYS = 7;
export const TREND_DAYS = 28;

export interface Forecast {
  /** прогноз на конец месяца; null — режим недоступен */
  value: number | null;
  /** текущий дневной темп режима («Сейчас в день»); null — недоступен или месяц закрыт */
  perDay: number | null;
  /** почему недоступен (null — доступен) */
  reason: string | null;
}

export type Forecasts = Record<ForecastMode, Forecast>;

/** Дневной ряд показателя: числа или null (день без себестоимости). */
export type DailyValues = readonly (number | null)[];

const ok = (value: number, perDay: number | null): Forecast => ({ value, perDay, reason: null });
const no = (reason: string): Forecast => ({ value: null, perDay: null, reason });

/** Общие условия: нет факта → недоступно; закрыт (R = 0) → факт, темп 0. */
function precheck(fact: number | null, e: number, D: number): Forecast | null {
  if (fact === null) return no("нет факта");
  if (D - e <= 0) return ok(fact, 0);
  return null;
}

/** Последние `need` значений ряда без null, иначе причина недоступности. */
function windowOf(values: DailyValues | null, need: number): number[] | string {
  if (values === null) return "нет дневного ряда";
  if (values.length < need) return `мало истории: ${values.length} из ${need} дн`;
  const w = values.slice(values.length - need);
  if (w.some((v) => v === null)) return "в окне есть дни без себестоимости";
  return w as number[];
}

export function forecastAvg(fact: number | null, e: number, D: number): Forecast {
  const pre = precheck(fact, e, D);
  if (pre) return pre;
  if (e <= 0) return no("нет дней месяца в данных");
  const f = fact as number;
  return ok(f + (f / e) * (D - e), f / e);
}

export function forecastLast7(fact: number | null, e: number, D: number, values: DailyValues | null): Forecast {
  const pre = precheck(fact, e, D);
  if (pre) return pre;
  const w = windowOf(values, LAST_DAYS);
  if (typeof w === "string") return no(w);
  const mean = w.reduce((s, v) => s + v, 0) / LAST_DAYS;
  return ok((fact as number) + mean * (D - e), mean);
}

/** МНК-прямая y = a + b·x по точкам x = 0…n−1. */
export function olsLine(y: readonly number[]): { a: number; b: number } {
  const n = y.length;
  const mx = (n - 1) / 2;
  const my = y.reduce((s, v) => s + v, 0) / n;
  let sxy = 0;
  let sxx = 0;
  y.forEach((v, x) => {
    sxy += (x - mx) * (v - my);
    sxx += (x - mx) ** 2;
  });
  const b = sxx === 0 ? 0 : sxy / sxx;
  return { a: my - b * mx, b };
}

export function forecastTrend(fact: number | null, e: number, D: number, values: DailyValues | null): Forecast {
  const pre = precheck(fact, e, D);
  if (pre) return pre;
  const w = windowOf(values, TREND_DAYS);
  if (typeof w === "string") return no(w);
  const { a, b } = olsLine(w);
  const R = D - e;
  const last = TREND_DAYS - 1;
  return ok((fact as number) + R * a + b * (last * R + (R * (R + 1)) / 2), a + b * TREND_DAYS);
}

/** Все три режима для факта и ряда участника. */
export function forecastsOf(fact: number | null, e: number, D: number, values: DailyValues | null): Forecasts {
  return {
    avg: forecastAvg(fact, e, D),
    last7: forecastLast7(fact, e, D, values),
    trend: forecastTrend(fact, e, D, values),
  };
}

/** Итог по участникам (у каждого — свой прогноз): Σ по режиму; недоступен хотя бы у одного — итог
 *  этого режима недоступен с причиной «кто: почему» (не сумма части участников). Участников нет —
 *  «нет факта». */
export function sumForecasts(parts: { who: string; fc: Forecasts }[]): Forecasts {
  const one = (mode: ForecastMode): Forecast => {
    if (parts.length === 0) return no("нет факта");
    const bad = parts.filter((p) => p.fc[mode].value === null);
    if (bad.length) return no(bad.map((p) => `${p.who}: ${p.fc[mode].reason ?? "недоступен"}`).join("; "));
    const perDays = parts.map((p) => p.fc[mode].perDay);
    const perDay = perDays.some((v) => v === null) ? null : perDays.reduce((s: number, v) => s + (v as number), 0);
    return ok(
      parts.reduce((s, p) => s + (p.fc[mode].value as number), 0),
      perDay,
    );
  };
  return { avg: one("avg"), last7: one("last7"), trend: one("trend") };
}

export function parseForecastMode(v: string | null): ForecastMode {
  return FORECAST_MODES.some((m) => m.id === v) ? (v as ForecastMode) : DEFAULT_FORECAST_MODE;
}

export function forecastModeLabel(mode: ForecastMode): string {
  return FORECAST_MODES.find((m) => m.id === mode)!.label;
}
